import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';
import {
  CHILDREN_COUNT_FIELD,
  DEPTH_FIELD,
  SELF_VALUE_FIELD,
  VALUE_FIELD,
  XY_DEFAULTS,
} from '../constants.js';

type AggregationAccessors = {
  idAccessor: string;
  parentIdAccessor: string;
  labelAccessor: string;
  valueAccessor: string;
  selfValueAccessor?: string;
  /**
   * Accessor used to sort sibling spans. Defaults to `labelAccessor`.
   */
  sortAccessor?: string;
};

type SpanNode = {
  span: ChartDatapoint;
  value: number;
};

type Frame = { node: SpanNode; x0: number; depth: number };

const toFiniteNumber = (raw: unknown): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Converts raw span data into flame graph rows: each span becomes one row with
 * computed `x0`/`x1` geometry (from inclusive value), depth, and children count.
 *
 * Numeric values are parsed once per span and cached on an intermediate node so
 * the sort comparator and layout pass do not re-run `Number()`. Layout uses an
 * explicit stack rather than recursion to stay safe on very deep call trees.
 */
export function aggregateSpansToFlameGraph(
  spans: ChartDatapoint[],
  {
    idAccessor,
    parentIdAccessor,
    labelAccessor,
    valueAccessor,
    selfValueAccessor,
    sortAccessor = labelAccessor,
  }: AggregationAccessors,
): ChartDatapoint[] {
  const readId = makeAccessor(idAccessor);
  const readParentId = makeAccessor(parentIdAccessor);
  const readValue = makeAccessor(valueAccessor);
  const readSort = makeAccessor(sortAccessor);
  const readSelfValue = makeAccessor(selfValueAccessor);

  const spanIds = new Set<unknown>();
  for (const span of spans) {
    spanIds.add(readId(span));
  }

  const childrenByParentId = new Map<unknown, SpanNode[]>();
  const roots: SpanNode[] = [];

  for (const span of spans) {
    const node: SpanNode = {
      span,
      value: toFiniteNumber(readValue(span)),
    };
    const parentId = readParentId(span);
    const isRoot =
      parentId === null || parentId === undefined || !spanIds.has(parentId);

    if (isRoot) {
      roots.push(node);
      continue;
    }

    const siblings = childrenByParentId.get(parentId);
    if (siblings) {
      siblings.push(node);
    } else {
      childrenByParentId.set(parentId, [node]);
    }
  }

  const compareBySortAccessor = (a: SpanNode, b: SpanNode) => {
    const av = readSort(a.span);
    const bv = readSort(b.span);
    if (typeof av === 'number' && typeof bv === 'number') {
      return av - bv;
    }
    return String(av ?? '').localeCompare(String(bv ?? ''));
  };

  if (roots.length > 1) {
    roots.sort(compareBySortAccessor);
  }
  for (const siblings of childrenByParentId.values()) {
    if (siblings.length > 1) {
      siblings.sort(compareBySortAccessor);
    }
  }

  const result: ChartDatapoint[] = [];
  const stack: Frame[] = [];
  let cursor = 0;
  for (const root of roots) {
    stack.push({ node: root, x0: cursor, depth: 0 });
    cursor += root.value;
  }
  stack.reverse();

  for (let frame = stack.pop(); frame !== undefined; frame = stack.pop()) {
    const { node, x0, depth } = frame;
    const { span, value } = node;
    const children = childrenByParentId.get(readId(span)) ?? [];

    const row: ChartDatapoint = {
      ...span,
      [XY_DEFAULTS.x0Accessor]: x0,
      [XY_DEFAULTS.x1Accessor]: x0 + value,
      [DEPTH_FIELD]: depth,
      [VALUE_FIELD]: value,
      [CHILDREN_COUNT_FIELD]: children.length,
    };
    if (readSelfValue !== undefined) {
      row[SELF_VALUE_FIELD] = toFiniteNumber(readSelfValue(span));
    }
    result.push(row);

    if (children.length === 0) {
      continue;
    }

    const frames: Frame[] = new Array(children.length);
    let childCursor = x0;
    for (let i = 0; i < children.length; i++) {
      frames[i] = { node: children[i], x0: childCursor, depth: depth + 1 };
      childCursor += children[i].value;
    }
    for (let i = frames.length - 1; i >= 0; i--) {
      stack.push(frames[i]);
    }
  }

  return result;
}
